
import { useEffect, useState } from 'react';
import { LoadingState, LoadingPriority } from './types';
import { LoadingStateManager } from './LoadingStateManager';

/**
 * React hook to access the loading state manager
 */
export const useLoadingState = () => {
  const [state, setState] = useState<LoadingState>({
    isLoading: false,
    pendingRequests: [],
  });
  const [networkStatus, setNetworkStatus] = useState<{ isLowBandwidth: boolean }>({
    isLowBandwidth: false
  });

  useEffect(() => {
    // Subscribe to loading state changes
    const unsubscribe = LoadingStateManager.getInstance().subscribe(setState);
    
    // Get initial network status
    setNetworkStatus(LoadingStateManager.getInstance().getNetworkStatus());
    
    // Set up periodic network status check
    const networkCheckInterval = setInterval(() => {
      setNetworkStatus(LoadingStateManager.getInstance().getNetworkStatus());
    }, 60000); // Check every minute
    
    return () => {
      unsubscribe();
      clearInterval(networkCheckInterval);
    };
  }, []);

  const startLoading = (
    name: string,
    priority: LoadingPriority = 'normal',
    dependencies?: string[]
  ) => {
    return LoadingStateManager.getInstance().addRequest(name, priority, dependencies);
  };

  const stopLoading = (id: string) => {
    LoadingStateManager.getInstance().completeRequest(id);
  };

  const clearAllLoading = () => {
    LoadingStateManager.getInstance().clearAllRequests();
  };

  const createLoadingBatch = (
    requests: Array<{ name: string; priority?: LoadingPriority }>,
    batchName: string
  ) => {
    return LoadingStateManager.getInstance().createBatch(requests, batchName);
  };

  const completeLoadingBatch = (requestIds: string[]) => {
    LoadingStateManager.getInstance().completeBatch(requestIds);
  };

  return {
    ...state,
    networkStatus,
    startLoading,
    stopLoading,
    clearAllLoading,
    createLoadingBatch,
    completeLoadingBatch
  };
};
